import React, {useState, useEffect, useRef} from 'react';
import ReactDOM from 'react-dom';
import classes from './ContentDetails.module.scss';
import Button from '../UI/Button/Button';
import axios from '../../axios-instance';
import AppleWallet from '../../images/add_to_apple_wallet.svg';
import GoogleWallet from '../../images/add_to_google_wallet.svg';
import ResponsiveDialog from '../UI/ResponsiveDialog/ResponsiveDialog';
import Scratcher from '../Scratcher/Scratcher';
import OfferDetails from './OfferDetails/OfferDetails';
import AgeGate from './../AgeGate/AgeGate';
import Loader from '../UI/Loader/Loader';
import { useSelector } from 'react-redux';
import DOMPurify from 'dompurify';
import { formatMDY, formatYMD, icsStampUTC } from '../../utils/date';
import { copyToClipboard } from '../../utils/clipboard';
import { DEV_MODE, devLogImpression } from '../../dev/devMode';

const ContentDetails = props => {
    const { scratcherOverlayImage } = props;
    const [
        // showLoading
        ,setShowLoading] = useState(true);
    const [showPass, setShowPass] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [imageError, setImageError] = useState(false)
    const [showOfferDetails, setShowOfferDetails] = useState(false);
    const [hasTimer, setHasTimer] = useState(false);
    const [limitSeconds, setLimitSeconds] = useState(0);
    const [hideAt, setHideAt] = useState(0);
    const [running, setRunning] = useState(false);
    const [expDate, setExpDate] = useState(0);
    const [expired, setExpired] = useState(null);
    const [distance, setDistance] = useState(0);
    const [showAgeGate, setShowAgeGate] = useState(false);
    const [showActionButtons, setShowActionButtons] = useState(true);
    const [autoAccept, setAutoAccept] = useState(false);
    const [cardMode, setCardMode] = useState(false);
    // Engagement-actions row (Remind Me / Save / Share) below the primary CTA.
    // Default ON during 2.0 dev (sales showcase — see ADR 0002); the flag
    // mechanism stays so the dashboard can toggle it later. ?engage=0/false/off
    // forces it off; the 'engagement-actions' tag is a no-op on-signal for now.
    const [engagementActions, setEngagementActions] = useState(true);
    // Gate the card behind a full asset preload so the scratcher never flashes
    // its purple fallback gradient (the artwork is painted before the card shows).
    const [assetsReady, setAssetsReady] = useState(false);
    // Reveal pop/expand: scratchRevealed flips on scratch completion;
    // revealSettled flips after the expand transition so overflow can go visible.
    const [scratchRevealed, setScratchRevealed] = useState(false);
    const [revealSettled, setRevealSettled] = useState(false);

    const content = useSelector((state) => state.content);
    const device = useSelector((state) => state.device);

    let attachedClasses = [classes.ContentPreview, classes.Close];

    let actionClasses = [classes.Actions];

    useEffect(() => {
        if(content?.content?.age_gate_enabled) {
            setShowAgeGate(true);
        } 
        
        // Check tags. Guard with `|| []` — a content item with no tags can come
        // back as null/undefined, and `for...of undefined` throws (which would
        // abort the rest of this effect, including the timer setup below).
        for(const element of (content?.content?.tags || [])) {
            if(element.name === 'no-action-buttons') {
                setShowActionButtons(false)
            }
            if(element.name === 'auto-save') {
                setAutoAccept(true);
            }
            // A 'card' tag on the content switches the page to the floating-card
            // presentation (scratcher-card look). Verified live against the real
            // API: tags arrive as [{ name: 'card', ... }]. The ?card=1 URL param
            // below is the equivalent frontend-only override.
            if(element.name === 'card') {
                setCardMode(true);
            }
            // An 'engagement-actions' tag turns on the Remind Me / Save / Share
            // row beneath the CTA (?engage=1 below is the equivalent override).
            if(element.name === 'engagement-actions') {
                setEngagementActions(true);
            }
        }

        // Offers and prizes default to the floating-card presentation — scratcher
        // or not (a no-scratcher offer just renders as the card style). url and
        // message keep the standard layout; the 'card' tag above can still force
        // any type into the card look.
        const type = content?.content?.type;
        if (type === 'offer' || type === 'prize') {
            setCardMode(true);
        }

        // Frontend-only override on ANY slug (incl. real API content): ?card=1
        // forces the card look on, ?card=0 opts a redemption back out.
        const params = new URLSearchParams(window.location.search);
        const cardFalsy = ['0', 'false', 'no', 'off'];
        const cardParam = params.get('card');
        if (cardParam !== null) {
            setCardMode(!cardFalsy.includes(cardParam.toLowerCase()));
        }
        // Override for the engagement-actions row (default ON): ?engage=0/false/
        // off forces it off, ?engage=1 forces it on.
        const engageParam = params.get('engage');
        if (engageParam !== null) {
            setEngagementActions(!cardFalsy.includes(engageParam.toLowerCase()));
        }

        setHasTimer(content?.content?.redemption?.timer?.enabled ?? false);
        setLimitSeconds(content?.content?.redemption?.timer?.limit ?? 0);
        setHideAt(Number(content?.content?.redemption?.status?.hide_at) * 1000 ?? 0);
    }, [content]);

    useEffect(()=> {
        let countInterval;
        if (running) {
            const countDownDate = new Date(expDate).getTime();
            countInterval = setInterval(() => {
                const now = new Date().getTime();
                const distance = countDownDate - now;
                setDistance(distance)
                if (distance < 0) {
                    setExpired(true);
                    clearInterval(countInterval);
                } else {
                    setExpired(false)
                }
            }, 1000);
            return () =>  {
                setRunning(false);
                setExpDate(0)
                clearInterval(countInterval);
            }
        } else {
            setRunning(false);
            setExpDate(0)
            clearInterval(countInterval);
        }
    }, [expDate, running])

    // Sanitize dashboard-authored HTML before injecting it. DOMPurify keeps the
    // formatting tags brands use (<p>/<ul>/<a>/<font>/<strong>…) but strips
    // <script>, event handlers, and javascript: URLs — defense against stored XSS.
    const createMarkup = () => {
        return {__html: DOMPurify.sanitize(content?.content?.description || '')};
    }

    // Terms come from the content-terms endpoint as a rich-text `body` string
    // (same trust level as the description). Rendered as HTML now that they're
    // exposed inline beneath the CTA rather than inside a tap-to-open modal.
    const createTermsMarkup = () => {
        return {__html: DOMPurify.sanitize(content?.terms || '')};
    }

    // Card mode: light gray page canvas behind the floating card (index.scss).
    useEffect(() => {
        if (cardMode) {
            document.body.classList.add('a8-card-page');
            return () => document.body.classList.remove('a8-card-page');
        }
    }, [cardMode]);

    // Hold the 3-dot loader until everything that paints the first frame is ready:
    // the scratcher artwork (so its purple fallback never shows), the hero image,
    // and the async terms-text fetch (so the revealed content doesn't reflow). A
    // 6s safety timeout guarantees the loader can never hang on a slow/failed asset.
    useEffect(() => {
        if (assetsReady) return;
        // Only the scratcher experience needs the full preload (its artwork must be
        // ready before the card shows). Non-scratcher content renders immediately.
        if (!props.scratcher) { setAssetsReady(true); return; }
        let cancelled = false;
        const finish = () => { if (!cancelled) setAssetsReady(true); };

        const urls = [];
        if (content?.hero) urls.push(content.hero);
        if (scratcherOverlayImage) urls.push(scratcherOverlayImage);

        let pending = urls.length;
        const termsPending = !!content?.content?.content_term_id && !content?.terms;
        const maybeDone = () => { if (pending <= 0 && !termsPending) finish(); };

        urls.forEach((src) => {
            const im = new Image();
            // The scratcher artwork MUST be preloaded with the same crossOrigin
            // mode ScratcherOverlay uses ('anonymous'), or the browser caches it
            // under a different key and the overlay re-downloads it — flashing the
            // fallback gradient while it loads (the exact bug QA recorded).
            if (src === scratcherOverlayImage) im.crossOrigin = 'anonymous';
            im.onload = im.onerror = () => { pending -= 1; maybeDone(); };
            im.src = src;
        });
        maybeDone();

        const timeout = setTimeout(finish, 6000);
        return () => { cancelled = true; clearTimeout(timeout); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assetsReady, content?.hero, content?.terms, content?.content?.content_term_id, props.scratcher, scratcherOverlayImage]);

    // Scratch reveal → pop the card and expand it to full content. revealSettled
    // (after the expand transition) lets overflow return to visible so the page
    // scrolls naturally past the previously-clipped card. The timer is held in a
    // ref and cleared on unmount so it can't set state on a dead component.
    const settleTimerRef = useRef(null);
    const handleScratchReveal = () => {
        setScratchRevealed(true);
        settleTimerRef.current = setTimeout(() => setRevealSettled(true), 700);
    };
    useEffect(() => () => clearTimeout(settleTimerRef.current), []);

    // Arm the wallet-pass flow once content lands (the device/flag gating that
    // decides WHICH badge renders happens in the showPass effect below).
    useEffect(() => {
        setShowPass(true)
    }, [content?.androidPass])


    useEffect(() => {
        // If the auto-save tag is added to the content, an "accept" impression is added immediately so that the prize can be reserved. 
        if(autoAccept) {
            updateStatus(2);
        }        

        if (props.scratcher === true || props.scratcher === null) {
            toggleLoading(true);
        } else {
            // Delay the update by 2 seconds to avoid collision with catch impression tracking
            setTimeout(()=>{
                updateStatus(1);
            }, 2000);
            //toggleLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.scratcher, autoAccept]) 


    const utcDateConverter = (datestring) => {
        if(datestring) {
            const split = datestring.split(" ");
            // Date-only strings (no time part) pass through unchanged — gluing
            // "Tundefined Z" on would make moment render "Invalid date".
            if (!split[1]) return datestring;
            return split[0] + "T" + split[1] + "Z"
        } else{
            return;
        }
    }

    const toggleLoading = (bool) => {
        setShowLoading(bool);
    }

    const onTerms = () => {
        setShowModal(!showModal)
    }
    
    // eslint-disable-next-line no-undef
    const updateStatus = async status => {
        // Dev mode: log the impression in-page, never write to the backend.
        if (DEV_MODE) { devLogImpression(status); return; }
        try {
          const uri = `/device/${device?.deviceDetails?.uuid}/content/${content?.content?.id}?status=${status}`;
          await axios.put(
            uri,
            {},
            {
              headers: {
                'Content-Type': 'application/json',
                'x-device-uuid': `${device?.deviceDetails?.uuid}`
              }
            }
          ).then((res) => {
            if (res && res.status === 200) {
                if (res.data && res.data.message) {
                //   console.log('STATUS: ', status + ' ', res.data.message);
                }
              }
          });
        } catch (err) {
            console.error('Error: ', err);
        }
    };    

    const postEvent = async(event) => {

        try {
          await axios.post(`/event`, event, {
            headers: {
              'x-device-uuid': `${device?.deviceDetails?.uuid}`,
            }
          }).then((res) => {
            if (res && res.data) {
                console.log('Event tracked', res.data);
              }
          });

        } catch(err) {
          console.error('Error: ', err);
        }
    }

    const getAppleWalletPasskit = async () => {
        let root = process.env.NODE_ENV === 'development' ? process.env.REACT_APP_ACTV8_API_ROOT_DEV : process.env.REACT_APP_ACTV8_API_ROOT_PROD;
        let key = process.env.NODE_ENV === 'development' ? process.env.REACT_APP_ACTV8_API_KEY_DEV : process.env.REACT_APP_ACTV8_API_KEY_PROD;

        try {
          const uri = `${root}/device/${device?.deviceDetails?.id}/content/${content?.content?.id}/applepass`;
          fetch(uri, {
            headers: {
              'x-api-key': `${key}`,
              'x-api-version': `4.4`,
              'Accept': 'application/vnd.apple.pkpass',
              'x-device-uuid': `${device?.deviceDetails?.uuid}`
            }
          }).then(response => {            
            updateStatus(2);
            response.blob().then(blob => {
              let url = window.URL.createObjectURL(blob);
              let a = document.createElement('a');
              a.href = url;
              a.download = 'pass.pkpass';
              a.click();
            });
          });
        } catch (err) {
          console.error('Error: ', err);
        }
    };

    const createAppleWalletPasskit = async () => {
        try {
          const uri = `/device/${device?.deviceDetails?.id}/content/${content?.id}/applepass`;
          const res = await axios.post(
            uri,
            {"forward_params":props.forwardParams},
            {
              headers: {
                'Accept': 'application/vnd.apple.pkpass',
                'x-device-uuid': `${device?.deviceDetails?.uuid}`
              }
            }
          );
          if (res && res.data) {
            getAppleWalletPasskit();
          }
        } catch (err) {
          console.error(err);
        }
    };

    // Navigate to the partner URL only — NO impression. Used by "Get it at
    // {domain}" and OfferDetails' "Redeem Offer", which are plain navigation;
    // the Buy-Now click (status=5) is recorded once when the overlay opens.
    const navigateOnline = () => {
        let url = content?.content?.redemption?.url ?? '';
        // Appends forward params (i.e. macros) based on env setting
        if(url !== '' && process.env.REACT_APP_FORWARD_PARAMS === 'true') {
            url += url.indexOf('?') > -1 ? '&' : '?';
            url += props.forwardParams;
        }
        window.open(url,'_blank');
    }

    // Copy the redemption code (online code modal). Honest feedback: only say
    // "Copied" when the copy actually landed; otherwise tell the user so they
    // can long-press the visible code instead.
    const handleCopyCode = async () => {
        const codeValue = content?.content?.redemption?.code?.value ?? '';
        const ok = await copyToClipboard(codeValue);
        setCodeCopied(ok ? 'ok' : 'fail');
        setTimeout(() => setCodeCopied(false), 1500);
    };

    // Engagement actions (feature-flagged row). All frontend-only — no new API.
    const showEngageToast = (msg) => {
        setEngageToast(msg);
        setTimeout(() => setEngageToast(''), 2200);
    };

    // Share — native share sheet where available, copy-link fallback otherwise.
    const handleShare = async () => {
        const shareUrl = window.location.href;
        const shareTitle = content?.title || content?.brand || 'Check out this offer';
        try {
            if (navigator.share) {
                await navigator.share({ title: shareTitle, text: shareTitle, url: shareUrl });
            } else {
                await navigator.clipboard.writeText(shareUrl);
                showEngageToast('Link copied');
            }
        } catch (e) {
            // User dismissed the share sheet, or share/clipboard was blocked — no-op.
        }
    };

    // Save — reserve the offer via the existing status contract, confirm w/ toast.
    const handleSaveOffer = () => {
        updateStatus(2);
        showEngageToast('Saved');
    };

    // Add to Calendar — generate an enriched .ics event for the offer's expiry
    // (1-day-before alarm) and download it. Carries the description, terms, and
    // a link back to the offer so the holder can return. Entirely client-side.
    const handleAddToCalendar = () => {
        const icsEscape = (s) => String(s).replace(/([,;\\])/g, '\\$1').replace(/\r?\n/g, '\\n');
        // Rich text → plain text (description/terms are authored HTML).
        const stripHtml = (h) => String(h || '')
            .replace(/<[^>]*>/g, ' ')
            .replace(/&nbsp;/g, ' ')
            .replace(/&amp;/g, '&')
            .replace(/&#39;|&rsquo;|&lsquo;|&apos;/g, "'")
            .replace(/&quot;|&ldquo;|&rdquo;/g, '"')
            .replace(/\s+/g, ' ')
            .trim();

        const title = content?.title || 'Offer reminder';
        const offerUrl = window.location.href;
        const desc = stripHtml(content?.content?.description);
        const terms = stripHtml(content?.terms);
        // Keep terms from bloating the event; clip if very long.
        const termsClip = terms.length > 600 ? terms.slice(0, 600) + '…' : terms;
        const descParts = [];
        if (desc) descParts.push(desc);
        if (termsClip) descParts.push('Terms: ' + termsClip);
        descParts.push('Open the offer: ' + offerUrl);
        const fullDesc = descParts.join('\n\n');

        const expiry = content?.content?.end_at;
        const day = expiry ? new Date(utcDateConverter(expiry)) : new Date(Date.now() + 86400000);
        const dateStr = formatYMD(day);
        const stamp = icsStampUTC();
        const uid = `${content?.content?.id ?? 'offer'}-${dateStr}@actv8me`;
        const ics = [
            'BEGIN:VCALENDAR',
            'VERSION:2.0',
            'PRODID:-//ACTV8me//Catch//EN',
            'CALSCALE:GREGORIAN',
            'BEGIN:VEVENT',
            `UID:${uid}`,
            `DTSTAMP:${stamp}`,
            `DTSTART;VALUE=DATE:${dateStr}`,
            `SUMMARY:${icsEscape(title + ' — last day to redeem')}`,
            `DESCRIPTION:${icsEscape(fullDesc)}`,
            `URL:${icsEscape(offerUrl)}`,
            'BEGIN:VALARM',
            'TRIGGER:-P1D',
            'ACTION:DISPLAY',
            `DESCRIPTION:${icsEscape(title)}`,
            'END:VALARM',
            'END:VEVENT',
            'END:VCALENDAR',
        ].join('\r\n');
        const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'offer-reminder.ics';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        showEngageToast('Reminder added to your calendar');
    };

    const onClickThrough1 = () => {
        let url = content?.clickThroughBtnOne?.value ?? '';
        handleClickThrough(url);
    }

    const onClickThrough2 = () => {
        let url = content?.clickThroughBtnTwo?.value ?? '';
        handleClickThrough(url);
    }

    const handleClickThrough = (url, target = '_blank') => {
        const event = {
            category: "End-User Action",
            subcategory: "Content Button",
            action: "Click-Through",
            label: url,
        } 

        event.client_id = content?.content?.client_id ?? null;
        event.campaign_id = content?.content?.pivot?.campaign_id ?? null;
        event.trigger_id = content?.content?.pivot?.trigger_id ?? null;
        event.content_id = content?.content?.pivot?.content_id ?? null;

        updateStatus(8)
        postEvent(event);
        window.open(url, target);

    }

    const onImageError = () => {
        setImageError(true);
        toggleLoading(false);
    }
    const onViewOfferDetails = (type) => {
        setShowOfferDetails(true);
        if (type === 'instore' && !hasTimer) {
            updateStatus(4)
        }
    }
    const onCloseOfferDetails = () => {
        setShowOfferDetails(!showOfferDetails);
    }
    if (content?.content?.redemption?.method === 3) {
        actionClasses.push(classes.MoreBtnSpace)
    }
    if (props.open) {
        attachedClasses = [classes.ContentPreview, classes.Open];
    }
    if (cardMode) {
        attachedClasses.push(classes.CardMode);
    }
    // Compact floating scratch card: only when card mode AND a scratcher is
    // present. The card flies in as an 80–95vh playing-card the user scratches,
    // then pops/expands to full content on reveal (Revealed → Settled).
    const isScratchCard = cardMode && props.scratcher;
    if (isScratchCard) {
        attachedClasses.push(classes.ScratchCard);
        if (scratchRevealed) attachedClasses.push(classes.Revealed);
        if (revealSettled) attachedClasses.push(classes.Settled);
    }

    const [showApplePass, setShowApplePass] = useState(false);
    const [showGooglePass, setShowGooglePass] = useState(false);
    // "Added to your wallet" confirmation toast — fires after a real wallet
    // action (Apple .pkpass download / Google save link), ported from the
    // scratcher-package design.
    const [showWalletToast, setShowWalletToast] = useState(false);
    // Online redemption modal — opened from the "Buy Online" CTA: copy the code,
    // then "Get it at {site}" opens the partner URL. The in-store path is its own
    // modal (the OfferDetails redeem dialog), opened from the "In Store" CTA — the
    // two redemption methods are now fully separate buttons + modals.
    const [showOnlineModal, setShowOnlineModal] = useState(false);
    const [codeCopied, setCodeCopied] = useState(false);
    // Remind Me modal — offers the holder a calendar invite and/or Add to Wallet
    // (geofence + time reminder). See [[reminder-features-are-a-showcase]].
    const [showRemindModal, setShowRemindModal] = useState(false);
    // Lightweight confirmation toast for the engagement actions (Save / Remind /
    // copied-link fallback). Reuses the wallet toast styling.
    const [engageToast, setEngageToast] = useState('');

    // Apple Wallet is iPhone-only by design (not iPad/macOS — deviceDetails
    // .isIphone is a UA "iphone" match); every other device gets the Google
    // badge (which still needs a jwt at render time).
    useEffect(() => {
        if (showPass === true) {
            if (
                device?.deviceDetails?.isMobile &&
                device?.deviceDetails?.isIphone &&
                content?.applePass
            ) {
                setShowApplePass(true);
            } else {
                setShowGooglePass(true);
            }
        }
    }, [
        content,
        content?.applePass,
        device,
        device?.isIphone,
        device?.isMobile,
        showPass,
    ]);

    const handleAgeEntered = (isLegal) => {
        setShowAgeGate(!isLegal);
    }

    // URL-type link buttons: white background, black text, drop shadow (design
    // system). Capped at two so a long list can't wall off the card. The bare
    // content.url "Click Here" fallback is intentionally dropped — it produced a
    // spurious extra link alongside the real labelled urls.
    const urls = content?.content?.urls
        ? content?.content?.urls.slice(0, 2).map((url) => (
            <Button
                className={classes.Button}
                style={{ backgroundColor: '#FFFFFF', color: '#000000' }}
                href={url.url}
                key={url.url}
                target={url.target ?? '_self'}
                onClick={() => handleClickThrough(url.url, url.target ?? '_self')}
                btnText={url.label}
            />
        ))
        : null;

    const urlActionButtons = (
        <div className={actionClasses.join(' ')}>
            {urls?.length > 0 && urls}
            {/* Click-through CTAs belong to the URL type only — same white
                button treatment as the link buttons above. */}
            {content?.clickThroughBtnOne && (
                <Button
                    className={classes.Button}
                    style={{ backgroundColor: '#FFFFFF', color: '#000000' }}
                    href={content?.clickThroughBtnOne.value || ''}
                    target='_blank'
                    onClick={onClickThrough1}
                    btnText={content?.clickThroughBtnOne.label || ''}
                />
            )}
            {content?.clickThroughBtnTwo && (
                <Button
                    className={classes.Button}
                    style={{ backgroundColor: '#FFFFFF', color: '#000000' }}
                    href={content?.clickThroughBtnTwo.value || ''}
                    target='_blank'
                    onClick={onClickThrough2}
                    btnText={content?.clickThroughBtnTwo.label || ''}
                />
            )}
        </div>
    )
  
  
    // Two independent redemption paths, each its OWN button + modal:
    //   • Buy Online  → online modal (copy code → partner site)
    //   • In Store    → OfferDetails redeem modal (single-use gate → barcode)
    // Both render together when both exist. Prize is redeem-only and always
    // routes to the in-store/redeem modal (fixes the old online-only-prize
    // dead-button: hasOnline excluded prize, hasInStore needed method 2/3, so a
    // prize with neither got no button at all).
    const isPrize = content?.content?.type === 'prize';
    const hasOnline = !!(
        content?.buyOnline &&
        content?.buyOnline !== '' &&
        !isPrize &&
        !content?.showDownload
    );
    const hasInStore =
        content?.content?.redemption?.method === 2 ||
        content?.content?.redemption?.method === 3;

    // Offer / prize redemption: two independent CTAs. Click-through, app-download
    // and the QR-scanner button used to live here too — click-through now belongs
    // to the URL type only (see urlActionButtons), and app-download / QR-scanner
    // were retired per the 2.0 review.
    const genericActionButtons = (
        <div className={actionClasses.join(' ')}>
            {/* Buy Online — opens the online redemption modal. This tap IS the
                Buy-Now click → status=5. */}
            {hasOnline && (
                <Button
                    className={classes.Button}
                    backgroundColor='var(--a8-primary)'
                    onClick={() => { setShowOnlineModal(true); updateStatus(5); }}
                    btnText={content?.buyNowText || 'Buy Online'}
                />
            )}
            {/* In Store / Redeem Prize — opens the OfferDetails redeem modal
                (single-use gate → barcode to scan). Prize always shows this. */}
            {(hasInStore || isPrize) && (
                <Button
                    className={classes.Button}
                    backgroundColor='var(--a8-primary)'
                    onClick={() => onViewOfferDetails('instore')}
                    btnText={isPrize ? 'Redeem Prize' : (content?.buyInStoreText || 'In Store')}
                />
            )}
        </div>
    );

    const actionButtons = (
        <div>{content?.content?.type === 'url' || content?.content?.type === 'message' ? urlActionButtons : genericActionButtons}</div>
    )

    // Which wallet badge actually renders (device + flag + jwt gated). The
    // supporting label adapts to whichever badge is shown.
    const appleBadgeVisible = showApplePass === true && !!content?.content?.id;
    const googleBadgeVisible = showGooglePass && content?.jwt && content?.jwt !== '';
    const walletLabel =
        appleBadgeVisible && !googleBadgeVisible
            ? 'Save for later by adding to your Apple Wallet'
            : googleBadgeVisible && !appleBadgeVisible
            ? 'Save for later by adding to your Google Wallet'
            : 'Save for later by adding to your wallet';

    const offerComp = (
        <div
            className={classes.ContentContainer}
            id={'responsiveContentContainer'}>
            <ResponsiveDialog open={showModal} onClose={() => onTerms()}>
                {content?.terms}
            </ResponsiveDialog>
            <ResponsiveDialog
                offerDetailsModal={true}
                open={showOfferDetails}
                onClose={() => onCloseOfferDetails()}>
                <OfferDetails
                    onUpdateStatus={updateStatus}
                    brand={content?.brand}
                    content={content?.content}
                    hasTimer={hasTimer}
                    hideAt={hideAt}
                    limitSeconds={limitSeconds}
                    onSetExpDate={(date) => setExpDate(date)}
                    distance={distance}
                    running={running}
                    onSetRunning={(bool) => setRunning(bool)}
                    expired={expired}
                    onClose={() => onCloseOfferDetails()}
                    onBuyNow={navigateOnline}
                />
            </ResponsiveDialog>
            {/* "Added to your wallet" confirmation toast. Portalled to <body> so a
                CSS transform on an ancestor (the scratch card) can't make the
                fixed overlay center on the card instead of the viewport. */}
            {showWalletToast && ReactDOM.createPortal(
                <div className={classes.ToastScrim} role='dialog' aria-modal='true'>
                    <div className={classes.Toast}>
                        <p className={classes.ToastMsg}>Added to your wallet</p>
                        <button
                            type='button'
                            className={classes.ToastOk}
                            onClick={() => setShowWalletToast(false)}>
                            OK
                        </button>
                    </div>
                </div>,
                document.body
            )}
            {/* Remind Me modal — calendar invite + Add to Wallet (geofence + time
                reminders). Wallet is the same device-gated pass used elsewhere. */}
            {showRemindModal && ReactDOM.createPortal(
                <div
                    className={classes.CodeScrim}
                    role='dialog'
                    aria-modal='true'
                    onClick={(e) => { if (e.target === e.currentTarget) setShowRemindModal(false); }}>
                    <div className={classes.CodeModal}>
                        <button
                            type='button'
                            className={classes.CodeClose}
                            onClick={() => setShowRemindModal(false)}
                            aria-label='Close'>
                            <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round'>
                                <path d='M6 6l12 12M18 6L6 18' />
                            </svg>
                        </button>
                        <h2 className={classes.CodeModalTitle}>Remind Me</h2>
                        <p className={classes.CodeModalSub}>
                            Get a nudge before this offer expires — add it to your calendar, or to your
                            wallet for location and time reminders.
                        </p>
                        <button
                            type='button'
                            className={classes.CodeModalRedeem}
                            onClick={() => { handleAddToCalendar(); setShowRemindModal(false); }}>
                            <svg className={classes.BtnIcon} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.9' strokeLinecap='round' strokeLinejoin='round'>
                                <rect x='3' y='4' width='18' height='18' rx='2' />
                                <path d='M16 2v4M8 2v4M3 10h18' />
                            </svg>
                            Add to Calendar
                        </button>
                        {(appleBadgeVisible || googleBadgeVisible) && (
                            <div className={classes.RemindWallet}>
                                {appleBadgeVisible && (
                                    <img
                                        src={AppleWallet}
                                        alt='Add to Apple Wallet'
                                        className={classes.RemindWalletBadge}
                                        onClick={() => createAppleWalletPasskit()}
                                    />
                                )}
                                {googleBadgeVisible && (
                                    <a
                                        href={'https://pay.google.com/gp/v/save/' + content.jwt}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        onClick={() => updateStatus(2)}>
                                        <img
                                            src={GoogleWallet}
                                            alt='Add to Google Wallet'
                                            className={classes.RemindWalletBadge}
                                        />
                                    </a>
                                )}
                            </div>
                        )}
                    </div>
                </div>,
                document.body
            )}
            {/* Engagement-action confirmation toast (Save / Remind / copied link).
                Auto-dismisses; lighter than the wallet toast (no OK button). */}
            {engageToast && ReactDOM.createPortal(
                <div className={classes.ToastScrim} role='status' aria-live='polite'>
                    <div className={classes.Toast}>
                        <p className={classes.ToastMsg} style={{ marginBottom: 0 }}>{engageToast}</p>
                    </div>
                </div>,
                document.body
            )}
            {/* Online redemption modal — opened by "Buy Online". Header + copy +
                the promo code (tap to copy) + a "Get it at {domain}" button that
                copies the code and opens the partner site. No section icons; a
                generic external-link warning (no brand name) sits below. */}
            {showOnlineModal && (() => {
                const onlineUrl = content?.content?.redemption?.url ?? content?.buyOnline ?? '';
                let onlineDomain = '';
                try { if (onlineUrl) onlineDomain = new URL(onlineUrl).hostname.replace(/^www\./, ''); } catch (e) { /* noop */ }
                const codeValue = content?.content?.redemption?.code?.value ?? '';
                const closeOverlay = () => setShowOnlineModal(false);
                return ReactDOM.createPortal(
                    <div
                        className={classes.CodeScrim}
                        role='dialog'
                        aria-modal='true'
                        onClick={(e) => { if (e.target === e.currentTarget) closeOverlay(); }}>
                        <div className={classes.CodeModal}>
                            <button
                                type='button'
                                className={classes.CodeClose}
                                onClick={closeOverlay}
                                aria-label='Close'>
                                <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round'>
                                    <path d='M6 6l12 12M18 6L6 18' />
                                </svg>
                            </button>

                            <h2 className={classes.CodeModalTitle}>
                                {codeValue !== '' ? 'Your Promo Code' : 'Shop Online'}
                            </h2>
                            <p className={classes.CodeModalSub}>
                                {codeValue !== ''
                                    ? 'Tap the code below to copy it, then use it at checkout.'
                                    : 'Continue to the online store to redeem this offer.'}
                            </p>

                            {codeValue !== '' && (
                                <button
                                    type='button'
                                    className={classes.CodeModalPill}
                                    onClick={handleCopyCode}
                                    aria-label='Copy promo code'>
                                    <span className={classes.CodeModalText}>{codeValue}</span>
                                    <span className={classes.CodeModalCopyHint}>
                                        {codeCopied === 'ok' ? 'Copied ✓' : codeCopied === 'fail' ? 'Copy failed — long-press the code' : 'Tap to copy'}
                                    </span>
                                </button>
                            )}

                            <button
                                type='button'
                                className={classes.CodeModalRedeem}
                                onClick={() => { if (codeValue !== '') handleCopyCode(); navigateOnline(); closeOverlay(); }}>
                                {onlineDomain ? `Get it at ${onlineDomain}` : 'Continue to site'}
                            </button>

                            <p className={classes.CodeModalNote}>
                                Tapping the button above takes you to an external website that is
                                not affiliated with this offer.
                                {codeValue !== '' && ' Your code will be copied to your clipboard.'}
                            </p>
                        </div>
                    </div>,
                    document.body
                );
            })()}
            <div>
                {!showAgeGate && (!content?.content.display_preferences || content?.content.display_preferences?.hero) &&
                    <section className={classes.HeroContainer}>
                        {imageError ? (
                            <div className={classes.FallbackHero}>
                                <div>Image Loading Error</div>
                            </div>
                        ) : (
                            ''
                        )}
                        <div className={classes.HeroImage}>
                            {!imageError ? (
                                <img
                                    src={content?.hero}
                                    alt={content?.hero}
                                    // LCP element — tell the browser to fetch it first.
                                    fetchpriority='high'
                                    decoding='async'
                                    onLoad={() => toggleLoading(false)}
                                    onError={onImageError}
                                />
                            ) : (
                                ''
                            )}
                        </div>
                    </section>
                }
                {showAgeGate ? (
                    <div className={classes.AgeGateBox}>
                        <AgeGate onAgeEntered={handleAgeEntered} />
                    </div>
                ) : (
                    <div className={classes.ContentItems}>
                        {(!content?.content.display_preferences || content?.content.display_preferences?.brand) &&
                            <div className={classes.Brand}>
                                {content?.brand}
                            </div>
                        }
                        {(!content?.content.display_preferences || content?.content.display_preferences?.title) &&
                            <div className={classes.Title}>{content?.title}</div>
                        }
                        {/* Action buttons lead — pulled ABOVE the description so the
                            redemption CTAs sit directly under the title. */}
                        {(!content?.content.display_preferences || content?.content.display_preferences?.actionButtons) &&
                            showActionButtons && actionButtons
                        }
                        {/* Engagement-actions row (feature-flagged) — sits directly
                            beneath the primary CTA. */}
                        {engagementActions && (
                            <div className={classes.EngagementRow}>
                                <button type='button' className={classes.EngageBtn} onClick={() => setShowRemindModal(true)}>
                                    <span className={classes.EngageIcon}>
                                        <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
                                            <path d='M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9' />
                                            <path d='M13.7 21a2 2 0 0 1-3.4 0' />
                                        </svg>
                                    </span>
                                    <span className={classes.EngageLabel}>Remind Me</span>
                                </button>
                                <button type='button' className={classes.EngageBtn} onClick={handleSaveOffer}>
                                    <span className={classes.EngageIcon}>
                                        <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
                                            <path d='M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z' />
                                        </svg>
                                    </span>
                                    <span className={classes.EngageLabel}>Save</span>
                                </button>
                                <button type='button' className={classes.EngageBtn} onClick={handleShare}>
                                    <span className={classes.EngageIcon}>
                                        <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
                                            <path d='M22 2L11 13' />
                                            <path d='M22 2l-7 20-4-9-9-4 20-7z' />
                                        </svg>
                                    </span>
                                    <span className={classes.EngageLabel}>Share</span>
                                </button>
                            </div>
                        )}
                        {/* Separator between the buttons and the description, shown
                            only when both are present. */}
                        {showActionButtons &&
                            (!content?.content.display_preferences || content?.content.display_preferences?.actionButtons) &&
                            (!content?.content.display_preferences || content?.content.display_preferences?.description) &&
                            <hr className={classes.Divider} />
                        }
                        {(!content?.content.display_preferences || content?.content.display_preferences?.description) &&
                            <div className={classes.Description}>
                                <div dangerouslySetInnerHTML={createMarkup()}></div>
                            </div>
                        }
                        {/* Apple/Google wallet save actions with a supporting
                            label, grouped beneath the description. */}
                        {(appleBadgeVisible || googleBadgeVisible) && (
                            <div className={classes.WalletBlock}>
                                <p className={classes.WalletLabel}>{walletLabel}</p>
                                <div className={classes.PayBtn}>
                                    <div className={classes.BtnCol}>
                                        {appleBadgeVisible && (
                                            <img
                                                src={AppleWallet}
                                                alt='Add to Apple Wallet'
                                                className={classes.WalletButton}
                                                onClick={() => {
                                                    createAppleWalletPasskit();
                                                    setShowWalletToast(true);
                                                }}
                                            />
                                        )}
                                        {googleBadgeVisible && (
                                            <a
                                                href={'https://pay.google.com/gp/v/save/' + content.jwt}
                                                target='_blank'
                                                rel='noopener noreferrer'
                                                onClick={() => {
                                                    updateStatus(2);
                                                    setShowWalletToast(true);
                                                }}
                                            >
                                                <img
                                                    src={GoogleWallet}
                                                    alt='Add to Google Wallet'
                                                    className={classes.WalletButton}
                                                />
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                        {/* Expiry + full terms text exposed below the CTA as gray copy
                            (scratcher-package: terms shown inline, not behind a link). */}
                        {(!content?.content.display_preferences || content?.content.display_preferences?.expiration) &&
                            (content?.endDate === 'Expired' ? (
                                <div className={classes.ExpiredBadge}>Expired</div>
                            ) : (
                                <div className={classes.Date}>
                                    Expires on{' '}
                                    {formatMDY(new Date(utcDateConverter(content?.content?.end_at)))}
                                </div>
                            ))
                        }
                        {(content?.content.content_term_id && (!content?.content.display_preferences || content?.content.display_preferences?.termsAndConditions)) &&
                            <div className={classes.TermsExposed} dangerouslySetInnerHTML={createTermsMarkup()}></div>
                        }
                    </div>
                )}
            </div>

        </div>
        
    );

    const prizeComp = offerComp;


    const urlComp = (<div className={classes.ContentContainer} id={'responsiveContentContainer'}>
        <ResponsiveDialog
            open={showModal}
            onClose={() => onTerms()}>
                {content?.terms}
        </ResponsiveDialog>
        <div>
            {!showAgeGate && (!content?.content.display_preferences || content?.content.display_preferences?.hero) &&
                <section className={classes.HeroContainer}>
                    {imageError ? (
                        <div className={classes.FallbackHero}>
                            <div>Image Loading Error</div>
                        </div>
                    ) : (
                        ''
                    )}
                    <div className={classes.HeroImage}>
                        {!imageError ? (
                            <img
                                src={content?.hero}
                                alt={content?.hero}
                                onLoad={() => toggleLoading(false)}
                                onError={onImageError}
                            />
                        ) : (
                            ''
                        )}
                    </div>
                </section>
            }
            { showAgeGate ?
                <div className={classes.AgeGateBox}>
                    <AgeGate onAgeEntered={handleAgeEntered}/>
                </div>
                :   <div className={`${classes.ContentItems} ${classes.UrlItems}`}>
                        {/* URL type: centered title + small centered description, no
                            brand, no expiry. Links are 1–2 white buttons. */}
                        {(!content?.content.display_preferences || content?.content.display_preferences?.title) &&
                            <div className={classes.UrlTitle}>{content?.title}</div>
                        }
                        {(!content?.content.display_preferences || content?.content.display_preferences?.description) &&
                            <div className={classes.UrlDescription}>
                                <div dangerouslySetInnerHTML={createMarkup()}></div>
                            </div>
                        }
                        {(!content?.content.display_preferences || content?.content.display_preferences?.actionButtons) &&
                            showActionButtons && actionButtons
                        }
                        {(content?.content.content_term_id && (!content?.content.display_preferences || content?.content.display_preferences?.termsAndConditions)) &&
                            <div className={classes.TermsExposed} dangerouslySetInnerHTML={createTermsMarkup()}></div>
                        }
                    </div>

            }

        </div>
    </div>)

    const messageComp = (<div className={classes.ContentContainer} id={'responsiveContentContainer'}>
        <ResponsiveDialog
            open={showModal}
            onClose={() => onTerms()}>
                {content?.terms}
        </ResponsiveDialog>
        <div>
            {!showAgeGate &&
                <section className={classes.HeroContainer}>
                    { imageError ? <div className={classes.FallbackHero}><div>Image Loading Error</div></div> : ''}
                    <div className={classes.HeroImage}>
                        { !imageError ?
                            <img
                                src={content?.hero}
                                alt={content?.hero}
                                onLoad={() => toggleLoading(false)}
                                onError={onImageError}/> : ''
                        }
                    </div>
                </section>
            }
            { showAgeGate ?
                <div className={classes.AgeGateBox}>
                    <AgeGate onAgeEntered={handleAgeEntered}/>
                </div>
                :
                /* Structured message body: title → description → in-body image →
                   actions, all inside the padded content column (no longer a bare
                   hero with the buttons floating loose beneath it). */
                <div className={classes.ContentItems}>
                    {content?.title &&
                        <div className={classes.Title}>{content?.title}</div>
                    }
                    {content?.content?.description &&
                        <div className={classes.Description}>
                            <div dangerouslySetInnerHTML={createMarkup()}></div>
                        </div>
                    }
                    {content?.content?.images?.in_body?.url &&
                        <section className={classes.InBodyContainer}>
                            <div className={classes.InBodyImage}>
                                <img
                                    src={content?.content?.images.in_body.url}
                                    alt={content?.title || 'Message image'}/>
                            </div>
                        </section>
                    }
                    {showActionButtons && actionButtons}
                </div>
            }
        </div>
    </div>)

    let contentComp = null;
    switch (content?.content?.type) {
        case 'offer': contentComp = offerComp; break;
        case 'url': contentComp = urlComp; break;
        case 'message': contentComp = messageComp; break;
        case 'prize': contentComp = prizeComp; break;
        default:
    }

    // Keep the 3-dot loader (same markup as Content.js) until every first-frame
    // asset is ready — the card then flies in fully painted, no purple flash.
    if (!assetsReady) {
        return (
            <div className='LoadContainer'>
                <div className='Loading'>
                    <Loader />
                </div>
            </div>
        );
    }

    const cardEl = (
        <div
            className={attachedClasses.join(' ')}
            style={cardMode ? undefined : { maxHeight: '736px', width: 'auto'}}>
            {props.scratcher ? (
                <Scratcher
                    onUpdateStatus={updateStatus}
                    onToggleLoading={toggleLoading}
                    onReveal={handleScratchReveal}
                    compact={isScratchCard}
                    overlay={scratcherOverlayImage}>
                    <div style={{height: "auto"}}>
                        {contentComp}
                    </div>
                </Scratcher>
            ) : (

                <div style={{height: "auto"}}>
                    {contentComp}
                </div>
            )}

        </div>
    );

    if (!cardMode) {
        return cardEl;
    }

    // Card mode: a flex "stage" column that fills the viewport. CardStageMain
    // grows to fill the space and centers the card within it (so a short
    // pre-reveal scratch card / short offer sits in the middle of the page on
    // desktop); the footer is pushed to the bottom. When the revealed card
    // grows taller than the viewport, the main area grows with it and the
    // footer flows below — always at the page bottom, never overlapping.
    // Center ONLY the short pre-reveal scratch card (the floating playing-card).
    // Once it reveals (or for a plain non-scratcher card), top-align so a tall
    // card scrolls from the top instead of being clipped by flex centering.
    const stageMainClasses = [classes.CardStageMain];
    if (isScratchCard && !scratchRevealed) {
        stageMainClasses.push(classes.CardStageMainCentered);
    }

    return (
        <div className={classes.CardStage}>
            <div className={stageMainClasses.join(' ')}>{cardEl}</div>
            <div className={classes.CardFooter}>
                Copyright © {new Date().getFullYear()} ACTV8me, Inc. All Rights Reserved.
            </div>
        </div>
    );
}

export default ContentDetails;

